(function (doc) {
  'use strict';

  // Match header responsive breakpoint (desktop nav vs hamburger)
  const DESKTOP_MIN_WIDTH = 769;

  // True when the desktop mega menu (not mobile drawer) should run
  const is_desktop = () => window.innerWidth >= DESKTOP_MIN_WIDTH;

  // Hamburger open/close and mobile accordion dropdowns
  function mobile_menu() {
    const menu_button = doc.querySelector('header nav button[aria-controls="nav-list"]');
    const nav_list = doc.querySelector('header nav ul#nav-list');

    // Bail if header markup is missing
    if (!nav_list || !menu_button) return;

    // Labels come from Liquid (translated) via data attributes
    const label_open = menu_button.getAttribute('data-label-open') || 'Open menu';
    const label_close = menu_button.getAttribute('data-label-close') || 'Close menu';

    // All top-level dropdown items in the mobile drawer
    const get_dropdowns = () => Array.from(nav_list.querySelectorAll('li.dropdown'));

    // Top-level link that toggles a mobile accordion panel
    const get_trigger = (dropdown) => dropdown.querySelector(':scope > a');

    // Accordion panel for a dropdown item
    const get_panel = (dropdown) => dropdown.querySelector(':scope > .dropdown-content');

    // Sync hamburger button label, icon, and aria-expanded
    const sync_menu_button = (is_open) => {
      const text = is_open ? label_close : label_open;
      const img = menu_button.querySelector('img');
      const label = menu_button.querySelector('span');

      menu_button.setAttribute('aria-label', text);
      menu_button.setAttribute('aria-expanded', String(is_open));

      // Swap hamburger / close icon
      if (img) {
        img.src = img.src.replace(
          is_open ? 'icon-menu.svg' : 'icon-close.svg',
          is_open ? 'icon-close.svg' : 'icon-menu.svg'
        );
        img.width = is_open ? 21 : 25;
        img.height = 21;
        img.alt = '';
      }

      // Keep visible label text in sync with open state
      if (label) label.textContent = text;
    };

    // Close one mobile accordion and sync ARIA
    const close_dropdown = (dropdown) => {
      // Nothing to close
      if (!dropdown) return;

      dropdown.classList.remove('active');

      const trigger = get_trigger(dropdown);
      const panel = get_panel(dropdown);

      // Mark trigger as collapsed
      if (trigger) trigger.setAttribute('aria-expanded', 'false');

      // Hide panel from assistive tech
      if (panel) panel.setAttribute('aria-hidden', 'true');
    };

    // Close every mobile accordion panel
    const close_all_dropdowns = () => {
      get_dropdowns().forEach(close_dropdown);
    };

    // Open one accordion (closes others first) and sync ARIA
    const open_dropdown = (dropdown) => {
      get_dropdowns().forEach((item) => {
        // Keep the requested dropdown open
        if (item === dropdown) return;

        close_dropdown(item);
      });

      dropdown.classList.add('active');

      const trigger = get_trigger(dropdown);
      const panel = get_panel(dropdown);

      // Mark trigger as expanded
      if (trigger) trigger.setAttribute('aria-expanded', 'true');

      // Expose panel to assistive tech
      if (panel) panel.setAttribute('aria-hidden', 'false');
    };

    // Toggle one accordion open/closed
    const toggle_dropdown = (dropdown) => {
      // Already open — collapse it
      if (dropdown.classList.contains('active')) {
        close_dropdown(dropdown);
        return;
      }

      open_dropdown(dropdown);
    };

    // Open or close the mobile nav drawer
    const set_drawer_open = (is_open) => {
      nav_list.classList.toggle('active', is_open);
      nav_list.setAttribute('aria-hidden', String(!is_open));
      sync_menu_button(is_open);

      // Closing the drawer also collapses any open accordion
      if (!is_open) close_all_dropdowns();
    };

    // Initial mobile state: drawer closed and hidden from AT when on small screens
    if (!is_desktop()) {
      nav_list.setAttribute('aria-hidden', 'true');
    }

    // Toggle the mobile nav drawer
    menu_button.addEventListener('click', function () {
      // Desktop uses hover/keyboard mega menus instead
      if (is_desktop()) return;

      set_drawer_open(!nav_list.classList.contains('active'));
    });

    // Accordion: tap a top-level dropdown link to expand its panel
    nav_list.addEventListener('click', function (e) {
      // Desktop does not use tap-to-toggle
      if (is_desktop()) return;

      const link = e.target.closest('li.dropdown > a');

      // Ignore clicks that are not on a dropdown trigger
      if (!link) return;

      // Stay on the page; open/close the panel instead of navigating
      e.preventDefault();

      toggle_dropdown(link.parentElement);
    });

    // Keyboard: Esc closes accordion then drawer; ArrowDown/Space toggle accordion
    nav_list.addEventListener('keydown', function (event) {
      // Desktop mega menu owns keyboard on large screens
      if (is_desktop()) return;

      // Drawer must be open for these keys to apply
      if (!nav_list.classList.contains('active')) return;

      const open_dropdown_el = get_dropdowns().find((item) => item.classList.contains('active'));

      // Esc: close open accordion first, otherwise close the drawer
      if (event.key === 'Escape') {
        event.preventDefault();

        // An accordion is open — collapse it and refocus its trigger
        if (open_dropdown_el) {
          const trigger = get_trigger(open_dropdown_el);
          close_dropdown(open_dropdown_el);
          trigger?.focus();
          return;
        }

        set_drawer_open(false);
        menu_button.focus();
        return;
      }

      const trigger = event.target.closest('li.dropdown > a');

      // Only ArrowDown/Space when focus is on a top-level trigger
      if (!trigger) return;

      const dropdown = trigger.closest('li.dropdown');

      // Safety: trigger should always sit inside li.dropdown
      if (!dropdown) return;

      // ArrowDown or Space toggles the accordion (Enter still follows click handler)
      if (event.key === 'ArrowDown' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        toggle_dropdown(dropdown);
      }
    });

    // Esc from the hamburger when the drawer is already open
    menu_button.addEventListener('keydown', function (event) {
      // Desktop does not use the hamburger drawer
      if (is_desktop()) return;

      // Only Esc while the drawer is open
      if (event.key !== 'Escape') return;
      if (!nav_list.classList.contains('active')) return;

      event.preventDefault();
      set_drawer_open(false);
      menu_button.focus();
    });

    // Crossing to desktop closes the drawer and clears mobile-only state
    window.addEventListener('resize', () => {
      // Still mobile — leave drawer state alone
      if (!is_desktop()) return;

      // Drawer was open — shut it and restore the hamburger
      if (nav_list.classList.contains('active')) {
        set_drawer_open(false);
      }

      // Desktop list is always visible; do not leave aria-hidden="true"
      nav_list.removeAttribute('aria-hidden');
    });
  }

  // Desktop mega menu keyboard support:
  // Tab across closed top-level items; ArrowDown/Space open; Tab-out/Esc close
  function desktop_mega_menu() {
    const nav_list = doc.querySelector('header nav ul#nav-list');

    // Bail if header nav list is missing
    if (!nav_list) return;

    // All top-level dropdown items
    const get_dropdowns = () => Array.from(nav_list.querySelectorAll('li.dropdown'));

    // Top-level link that opens a mega panel
    const get_trigger = (dropdown) => dropdown.querySelector(':scope > a');

    // Mega panel for a dropdown item
    const get_panel = (dropdown) => dropdown.querySelector(':scope > .dropdown-content');

    // Whether the panel is open via .active (keyboard/JS)
    const is_open = (dropdown) => dropdown.classList.contains('active');

    // Focusable controls inside an open mega panel
    const get_panel_focusables = (panel) => {
      // No panel means nothing to focus
      if (!panel) return [];

      return Array.from(
        panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true');
    };

    // Close one dropdown and sync ARIA
    // force_esc overrides CSS :hover so Esc still hides the panel under the cursor
    const close_dropdown = (dropdown, force_esc) => {
      // Nothing to close
      if (!dropdown) return;

      dropdown.classList.remove('active');

      // Stick closed until mouseenter clears this (hover would otherwise reopen it)
      if (force_esc) dropdown.classList.add('is-esc-closed');

      const trigger = get_trigger(dropdown);
      const panel = get_panel(dropdown);

      // Mark trigger as collapsed
      if (trigger) trigger.setAttribute('aria-expanded', 'false');

      // Hide panel from assistive tech
      if (panel) panel.setAttribute('aria-hidden', 'true');
    };

    // Close every dropdown, optionally leaving one open
    const close_all = (except) => {
      get_dropdowns().forEach((dropdown) => {
        // Keep the requested dropdown open
        if (except && dropdown === except) return;

        close_dropdown(dropdown);
      });
    };

    // Open one dropdown (closes others first) and sync ARIA
    const open_dropdown = (dropdown) => {
      close_all(dropdown);
      dropdown.classList.remove('is-esc-closed');
      dropdown.classList.add('active');

      const trigger = get_trigger(dropdown);
      const panel = get_panel(dropdown);

      // Mark trigger as expanded
      if (trigger) trigger.setAttribute('aria-expanded', 'true');

      // Expose panel to assistive tech
      if (panel) panel.setAttribute('aria-hidden', 'false');
    };

    // Move focus to the first link in the open panel
    const focus_first_panel_item = (dropdown) => {
      const panel = get_panel(dropdown);
      const focusables = get_panel_focusables(panel);

      // Empty panel — nothing to focus
      if (!focusables.length) return;

      // Wait a frame so display/visibility styles apply before focusing
      requestAnimationFrame(() => {
        focusables[0].focus();
      });
    };

    // Allow hover again after an Esc-forced close
    get_dropdowns().forEach((dropdown) => {
      dropdown.addEventListener('mouseenter', () => {
        dropdown.classList.remove('is-esc-closed');
      });
    });

    // Keyboard: Esc, ArrowDown, Space, ArrowUp on desktop nav
    nav_list.addEventListener('keydown', (event) => {
      // Mobile uses tap accordion instead
      if (!is_desktop()) return;

      // Prefer .active panel; fall back to the dropdown that currently has focus
      const open_dropdown_el =
        get_dropdowns().find(is_open) || event.target.closest('li.dropdown');

      // Esc closes the panel (even under hover) and returns focus to its trigger
      if (event.key === 'Escape' && open_dropdown_el) {
        event.preventDefault();
        close_dropdown(open_dropdown_el, true);
        get_trigger(open_dropdown_el)?.focus();
        return;
      }

      const trigger = event.target.closest('li.dropdown > a');

      // Only handle keys when focus is on a top-level dropdown trigger
      if (!trigger) return;

      const dropdown = trigger.closest('li.dropdown');

      // Safety: trigger should always sit inside li.dropdown
      if (!dropdown) return;

      // ArrowDown opens (if needed) and moves focus into the panel
      if (event.key === 'ArrowDown') {
        event.preventDefault();

        // Open first when the panel is still closed
        if (!is_open(dropdown)) open_dropdown(dropdown);

        focus_first_panel_item(dropdown);
        return;
      }

      // Space toggles the panel; focus stays on the trigger
      if (event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();

        // Already open — close it (override hover if needed)
        if (is_open(dropdown)) {
          close_dropdown(dropdown, true);
          return;
        }

        open_dropdown(dropdown);
        return;
      }

      // ArrowUp closes an open panel without leaving the trigger
      if (event.key === 'ArrowUp' && is_open(dropdown)) {
        event.preventDefault();
        close_dropdown(dropdown, true);
      }
    });

    // Tabbing out of an open dropdown closes that panel
    nav_list.addEventListener('focusout', (event) => {
      // Mobile does not use this focus pattern
      if (!is_desktop()) return;

      const dropdown = event.target.closest('li.dropdown');

      // Only act when leaving an open dropdown
      if (!dropdown || !is_open(dropdown)) return;

      const next = event.relatedTarget;

      // Focus still inside this dropdown — keep it open
      if (next && dropdown.contains(next)) return;

      close_dropdown(dropdown);
    });

    // Click outside any dropdown closes all open panels
    doc.addEventListener('click', (event) => {
      // Mobile accordion handles its own state
      if (!is_desktop()) return;

      // Click landed inside a dropdown — leave it alone
      if (event.target.closest('header nav ul#nav-list li.dropdown')) return;

      close_all();
    });

    // Crossing to mobile width clears desktop-only .active state
    // Mobile browsers fire resize while swiping (toolbar collapse), so only react to a real breakpoint cross
    let was_desktop = is_desktop();

    window.addEventListener('resize', () => {
      const now_desktop = is_desktop();

      // Breakpoint unchanged — leave open dropdowns alone
      if (now_desktop === was_desktop) return;

      was_desktop = now_desktop;

      // Still desktop — nothing to reset
      if (now_desktop) return;

      close_all();
    });
  }

  // First Tab enables keyboard/accessibility mode; mouse use turns it off
  function accessibility_mode() {
    // Add body class used for keyboard focus styles
    const enable_accessibility_mode = (event) => {
      // Only Tab (not other keys) should enter accessibility mode
      if (event.key !== 'Tab') return;

      doc.body.classList.add('accessibility-mode');
      doc.removeEventListener('keydown', enable_accessibility_mode);
    };

    // Pointer interaction exits accessibility mode and re-arms Tab listener
    const disable_accessibility_mode = () => {
      // Already off — nothing to do
      if (!doc.body.classList.contains('accessibility-mode')) return;

      doc.body.classList.remove('accessibility-mode');
      doc.addEventListener('keydown', enable_accessibility_mode);
    };

    doc.addEventListener('keydown', enable_accessibility_mode);
    doc.addEventListener('mousedown', disable_accessibility_mode);
  }

  mobile_menu();
  desktop_mega_menu();
  accessibility_mode();
}(document));
